/**
 * Handler: add_to_cart event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct, StorefrontCart } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { adaptCart } from '../../adapters/cart';
import { generateEventId, gtmMarketFields } from '../utils';
import { trackKlaviyoAddToCartDirect } from '../fallbacks/klaviyo';

export function handleAddToCart(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  quantity: number,
  cart: StorefrontCart,
  variantId?: string
): void {
  // Always use window.analyzify to ensure we have the current reference
  const win = window as any;
  const winAnalyzify = win.analyzify as WindowAnalyzify;
  if (!winAnalyzify) {
    console.warn('trackAddToCart: window.analyzify not found');
    return;
  }

  const { product: adaptedProduct, variant } = adaptProduct(product, variantId);
  const adaptedCart = adaptCart(cart);
  const eventId = generateEventId();
  // CDN add-to-cart functions take the quantity from product.quantity and
  // otherwise fall back to the first quantity input on the page (or 1).
  const eventProduct = { ...adaptedProduct, quantity: quantity || 1 };

  // Ensure collection is set (some CDN scripts like Klaviyo expect it)
  // Use empty collection if not on a collection page
  // CRITICAL: Must include ALL fields that CDN scripts expect, including products array
  // Otherwise CDN scripts calling collection.products.map() will throw TypeError
  if (!(winAnalyzify as any).collection) {
    (winAnalyzify as any).collection = {
      id: 0,
      title: '',
      handle: '',
      products: [],
      all_products_count: 0,
      products_count: 0,
    };
  }

  // GA4 - 2nd param should be variantId string, not variant object
  if (typeof winAnalyzify.gaAddToCart === 'function') {
    try {
      winAnalyzify.gaAddToCart({ product: eventProduct, variant }, variant.id.toString(), eventId);
    } catch (error) {
      console.warn('gaAddToCart error (non-critical):', error);
    }
  }

  // Facebook
  if (typeof winAnalyzify.fbAddToCart === 'function') {
    try {
      winAnalyzify.fbAddToCart(
        { product: eventProduct, variant },
        variant.id.toString(),
        eventId
      );
    } catch (error) {
      console.warn('fbAddToCart error (non-critical):', error);
    }
  }

  // TikTok
  if (typeof winAnalyzify.ttAddToCart === 'function') {
    try {
      winAnalyzify.ttAddToCart(
        { product: eventProduct, variant },
        variant.id.toString(),
        eventId
      );
    } catch (error) {
      console.warn('ttAddToCart error (non-critical):', error);
    }
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  let gtmTracked = false;
  if (typeof winAnalyzify.gtmAddToCart === 'function') {
    try {
      winAnalyzify.gtmAddToCart({ product: eventProduct, variant }, variant.id.toString(), eventId);
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && winAnalyzify.properties?.GTM?.status) {
    const collection = winAnalyzify.collection;
    const variantPrice = winAnalyzify.formatPrice?.(variant.price, true) ?? variant.price / 100;
    const compareAtPrice = variant.compare_at_price ? (winAnalyzify.formatPrice?.(variant.compare_at_price, true) || 0) : 0;
    const getDiscount = (compareAtPrice > 0 && compareAtPrice !== variantPrice) ? compareAtPrice - variantPrice : 0;
    const totalVal = parseFloat(((variantPrice || 0) * (quantity || 1)).toFixed(2));

    const itemObj: Record<string, any> = {
      id: winAnalyzify.getShopifyId?.(
        winAnalyzify.feed_region || '',
        adaptedProduct.id,
        variant.id,
        variant.sku
      ) || null,
      item_id: adaptedProduct.id?.toString() || null,
      item_name: adaptedProduct.title?.trim() || null,
      price: variantPrice,
      discount: winAnalyzify.formatPrice?.(getDiscount, false) || 0,
      item_barcode: variant.barcode || null,
      item_category: adaptedProduct.type || null,
      item_brand: adaptedProduct.vendor?.trim() || null,
      quantity: quantity || 1,
      item_variant: variant.title || null,
      item_variant_id: variant.id?.toString() || null,
      item_sku: variant.sku || null,
      index: 0,
    };

    if (collection) itemObj.item_list_id = (collection as any)?.id || null;
    if ((collection as any)?.title) itemObj.item_list_name = (collection as any)?.title || null;

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_add_to_cart',
      ss_event_id: eventId || winAnalyzify.eventId.get('add_to_cart') || null,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        currency: winAnalyzify.currency || null,
        value: winAnalyzify.formatPrice?.(totalVal, false) ?? totalVal,
        cart_id: winAnalyzify.cart_id || null,
        items: [itemObj],
      },
      ...gtmMarketFields(winAnalyzify),
    });
  }

  // Bing
  if (typeof winAnalyzify.bingAddtoCart === 'function') {
    try {
      winAnalyzify.bingAddtoCart({ product: eventProduct, variant }, variant.id.toString());
    } catch (error) {
      console.warn('bingAddtoCart error (non-critical):', error);
    }
  }

  // Pinterest
  if (typeof winAnalyzify.pinterestAddtoCart === 'function') {
    try {
      winAnalyzify.pinterestAddtoCart(
        { product: eventProduct, variant },
        variant.id.toString()
      );
    } catch (error) {
      console.warn('pinterestAddtoCart error (non-critical):', error);
    }
  }

  // Criteo
  if (typeof winAnalyzify.criteoAddToCart === 'function') {
    try {
      winAnalyzify.criteoAddToCart(
        { product: eventProduct, variant },
        variant.id.toString()
      );
    } catch (error) {
      console.warn('criteoAddToCart error (non-critical):', error);
    }
  }

  // Klaviyo - try CDN first, fall back to direct API
  // The CDN wrapper has a bug where it references bare variables (collection, taxonomy)
  // without destructuring them. When CDN fails, we call window.klaviyo directly.
  const klaviyoProps = analyzify.properties?.KLAVIYO;
  if (klaviyoProps?.status && klaviyoProps.events?.add_to_cart) {
    if (typeof winAnalyzify.klaviyoAddToCart === 'function') {
      try {
        winAnalyzify.klaviyoAddToCart(
          { product: eventProduct, variant },
          variant.id.toString()
        );
      } catch (error) {
        // CDN function failed - use direct Klaviyo API as fallback
        trackKlaviyoAddToCartDirect(analyzify, adaptedProduct, variant, quantity);
      }
    } else {
      // CDN function not defined - use direct API
      trackKlaviyoAddToCartDirect(analyzify, adaptedProduct, variant, quantity);
    }
  }

  // Snapchat
  if (typeof winAnalyzify.snapchatAddToCart === 'function') {
    try {
      winAnalyzify.snapchatAddToCart(
        { product: eventProduct, variant },
        variant.id.toString(),
        eventId
      );
    } catch (error) {
      console.warn('snapchatAddToCart error (non-critical):', error);
    }
  }

  // X
  if (typeof winAnalyzify.xAddtoCart === 'function') {
    try {
      winAnalyzify.xAddtoCart({ product: eventProduct, variant }, variant.id.toString());
    } catch (error) {
      console.warn('xAddtoCart error (non-critical):', error);
    }
  }

  // Server-side
  if (typeof winAnalyzify.ssAddToCart === 'function') {
    try {
      winAnalyzify.ssAddToCart(
        { product: eventProduct, variant },
        variant.id.toString(),
        eventId
      );
    } catch (error) {
      console.warn('ssAddToCart error (non-critical):', error);
    }
  }

  // Clarity
  if (typeof winAnalyzify.clarityAddToCart === 'function') {
    try {
      winAnalyzify.clarityAddToCart();
    } catch (error) {
      console.warn('clarityAddToCart error (non-critical):', error);
    }
  }

  // Hotjar
  if (typeof winAnalyzify.hotjarAddToCart === 'function') {
    try {
      winAnalyzify.hotjarAddToCart();
    } catch (error) {
      console.warn('hotjarAddToCart error (non-critical):', error);
    }
  }
}
